import React, { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import { useTheme } from '@/context/ThemeContext'
import { WorkspaceSetup, CompleteProfileStep, useOnboarding } from '@/features/onboarding'
import type { WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import OnboardingProfileSheet from '@/features/onboarding/components/OnboardingProfileSheet'
import { resolvePostLoginRoute } from '@/lib/resolvePostLoginRoute'

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const OnboardingPage: React.FC = () => {
  const { currentTheme } = useTheme()
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [user, setUser] = useState<{ name?: string; email?: string } | null>(null)

  useEffect(() => {
    const token = Cookies.get('token')
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    try {
      const raw = localStorage.getItem('user')
      if (raw) setUser(JSON.parse(raw))
    } catch {}
    resolvePostLoginRoute().then(({ path }) => {
      if (path === '/dashboard') {
        navigate('/dashboard', { replace: true })
      } else {
        setChecking(false)
      }
    })
  }, [navigate])

  const handleOpenSheet = useCallback(() => setSheetOpen(true), [])
  const handleCloseSheet = useCallback(() => setSheetOpen(false), [])

  const { workspace, updateWorkspace } = useOnboarding()
  const [step, setStep] = useState<'workspace' | 'profile'>('workspace')
  const [createdWorkspace, setCreatedWorkspace] = useState<WorkspaceSummary | null>(null)

  const handleWorkspaceCreated = useCallback((ws: WorkspaceSummary) => {
    setCreatedWorkspace(ws)
    setStep('profile')
  }, [])

  const initials = user?.name ? getInitials(user.name) : 'U'

  if (checking) {
    return (
      <div
        className="h-screen flex items-center justify-center"
        style={{ backgroundColor: currentTheme.primary }}
      >
        <Loader2 size={32} className="animate-spin" style={{ color: currentTheme.accent }} />
      </div>
    )
  }

  return (
    <div
      className="h-screen overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <div className="relative mx-auto h-full max-w-[1480px] px-4 py-5 xl:px-8 xl:py-6">
        {/* Profile avatar button */}
        <button
          type="button"
          onClick={handleOpenSheet}
          aria-label="Open account settings"
          className="absolute right-4 top-4 z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border shadow-sm transition-opacity hover:opacity-80 select-none"
          style={{
            backgroundColor: currentTheme.accent,
            color: '#fff',
            borderColor: currentTheme.border,
          }}
        >
          {initials}
        </button>

        <div className="grid h-full w-full gap-8 xl:grid-cols-[1.1fr_0.9fr] items-stretch">
          {/* Form panel */}
          <section
            className="overflow-y-auto overflow-x-hidden custom-scrollbar rounded-[32px] border shadow h-full flex flex-col"
            style={{
              boxShadow: currentTheme.boxShadow,
              backgroundColor: currentTheme.secondary,
              borderColor: currentTheme.border,
            }}
          >
            {step === 'workspace' ? (
              <WorkspaceSetup
                workspace={workspace}
                onUpdate={updateWorkspace}
                onCreated={handleWorkspaceCreated}
              />
            ) : (
              createdWorkspace && <CompleteProfileStep workspace={createdWorkspace} />
            )}
          </section>

          {/* Right panel — animated background + logo */}
          <aside
            className="relative overflow-hidden rounded-[32px] shadow-[0_32px_80px_rgba(15,23,42,0.25)] hidden xl:block"
            style={{ backgroundColor: currentTheme.secondary }}
          >
            <img
              src="https://media4.giphy.com/media/DIouyQ9xdybpAmRUVI/giphy.webp"
              alt="Animated growth illustration"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: 'rgba(15,23,42,0.72)' }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="flex items-center gap-4">
                <img
                  src="/Zeph-White-Logo.png"
                  alt="Zeph AI Logo"
                  className="w-16 h-16 object-contain"
                />
                <h2 className="text-4xl font-semibold text-white">Zeph AI</h2>
              </div>
              <p className="mt-4 text-sm text-white/60 text-center px-12">
                {step === 'workspace'
                  ? 'Set up your workspace in seconds and start generating better outreach, faster.'
                  : 'Almost there — a few details help Zeph do its best work.'}
              </p>
            </div>
          </aside>
        </div>
      </div>

      <OnboardingProfileSheet open={sheetOpen} onClose={handleCloseSheet} />
    </div>
  )
}

export default OnboardingPage
